<template>
  <transition name="notification-transition" @after-leave="destroyElement">
    <div
      v-show="isActive"
      v-on="$listeners"
      :class="[this.type && `notification--${this.type}`, this.classes, this.customCSSClasses]"
      :style="positionStyle"
      ref="notification"
      class="notification ev-digital-event-notification"
    >
      <div :style="wrapperStyle" class="notification__wrapper">
        <div class="notification__content">
          <span class="notification__message">
            <span v-if="Boolean(iconType) && !hideIcon" :style="iconStyle" class="notification__icon">
              <svg
                :viewBox="iconType.viewBox"
                :width="iconType.width"
                :height="iconType.height"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path :d="iconType.path" fill="currentColor" />
              </svg>
            </span>
            <p v-if="HTMLMessage" class="notification__text" v-html="HTMLMessage"></p>
            <p class="notification__text">{{ message }}</p>
          </span>
          <button v-if="actionText" class="notification__action" type="button" @click="onActionClick">
            {{ actionText }}
          </button>
          <button v-if="showClose" class="notification__close" type="button" @click="close">
            <svg :viewBox="NOTIFICATION_ICONS.close.viewBox" role="img" xmlns="http://www.w3.org/2000/svg">
              <path :d="NOTIFICATION_ICONS.close.path" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div v-if="buttons.length" class="notification__content notification__buttons-content">
          <button
            v-for="btn in buttons"
            :key="btn.name"
            class="notification__action"
            :class="'notification__' + btn.type"
            :style="btn.style"
            type="button"
            @click="btn.handler"
          >
            <template v-if="btn.icon"> <i :class="btn.icon" aria-hidden="true"></i>&nbsp; </template>
            {{ btn.name }}
            <template v-if="showCountdown && btn.showCountdown">
              <span>&nbsp;({{ countdown }})</span>
            </template>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import positionable from "../../mixins/positionable";
import toggleable from "../../mixins/toggleable";
import colorable from "../../mixins/colorable";

import { Z_INDEX_LIST, NOTIFICATION_ICONS, NOTIFICATION_THEME } from "../../constants";

export default {
  name: "NotificationItem",
  mixins: [positionable, toggleable, colorable],
  props: {
    // Can be : success / info / warning / error
    type: { type: String },
    hideIcon: { type: Boolean, default: false },
    // Text to show on notification
    message: { type: String, default: String },
    // A position name like (top-left) only used to categorize notification emplacement
    position: { type: String, default: String },
    // Makes the notification higher (mobile)
    multiLine: { type: Boolean, default: false },
    // Makes the notification full width like in mobile
    fullWidth: { type: Boolean, default: false },
    // Offset to show notification
    verticalOffset: { type: Number, default: 16 },
    // Delay before closing notification
    closeDelay: { type: Number, default: 4500 },
    // Action button
    actionText: { type: String, default: undefined },
    onActionClick: { type: Function, default: undefined },
    // show a close button
    showClose: { type: Boolean, default: false },
    // Theme
    theme: { type: Object, default: () => NOTIFICATION_THEME },
    // zindex
    zIndex: { type: Number, default: Z_INDEX_LIST.notification },
    // HTML messasge
    HTMLMessage: { type: String, default: null },
    // Button list
    buttons: { type: Array, default: () => [] },
    // Custom CSS classes
    customCSSClasses: { type: Array, default: () => [] },
    // Show a countdown
    showCountdown: { type: Number, default: null }
  },
  data: () => ({
    NOTIFICATION_ICONS,
    activeTimeout: -1,
    activeInterval: -1,
    countdown: 0
  }),
  computed: {
    classes() {
      return {
        "notification--active": this.isActive,
        "notification--multi-line": this.multiLine,
        "notification--absolute": this.absolute,
        "notification--bottom": this.bottom || !this.top,
        "notification--left": this.left,
        "notification--right": this.right,
        "notification--top": this.top,
        "notification--full-width": this.fullWidth
      };
    },
    verticalProperty() {
      return /^top/.test(this.position) ? "top" : "bottom";
    },
    positionStyle() {
      return {
        zIndex: this.zIndex,
        [this.verticalProperty]: `${this.verticalOffset}px`
      };
    },
    wrapperStyle() {
      const { colors, boxShadow } = this.theme;
      return {
        ...(colors && this.setBackgroundColor(colors[this.type])),
        ...(boxShadow && { "box-shadow": boxShadow })
      };
    },
    iconStyle() {
      const { colors } = this.theme;
      return {
        ...(colors && this.setBackgroundColor(colors[`${this.type}Darken`]))
      };
    },
    iconType() {
      return this.type && NOTIFICATION_ICONS[this.type];
    }
  },
  watch: {
    isActive: {
      async handler() {
        await this.$nextTick();
        this.setTimeout();
      },
      immediate: true
    }
  },
  methods: {
    setTimeout() {
      clearTimeout(this.activeTimeout);
      clearInterval(this.activeInterval);

      if (this.showCountdown) {
        this.countdown = this.showCountdown / 1000;
        this.activeInterval = setInterval(() => {
          this.countdown--;
        }, 1000);
      }
      if (!this.isActive || !this.closeDelay) return;
      this.activeTimeout = setTimeout(() => {
        this.isActive = false;
      }, this.closeDelay);
    },
    destroyElement() {
      this.$destroy(true);
      this.$el.parentNode.removeChild(this.$el);
    },
    close() {
      this.isActive = false;
    }
  }
};
</script>
